import { Tooltip } from "antd";
import { useIntl } from "react-intl";
import { Link } from "react-router";
import { useParams } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { dateTimeSecondsFormat } from "~constants/dateFormat.ts";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { FixturePlayByPlayEventColumns } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/enums/FixturePlayByPlayEventColumns.ts";
import type { FixturePlayByPlayEventTransformed } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/types/FixturePlayByPlayEventTransformed.ts";
import { SYS_APP_BASEURL } from "~shared/constants.ts";

export const useGetFixturePlayByPlayFlaggedEventDataTableColumns = (): Array<DataTableColumn<FixturePlayByPlayEventTransformed>> => {
  const { formatMessage, messages } = useIntl();
  const { periods, competitorType } = useSportConfiguration();
  const { sport } = useParams();

  const tooltipMessageId =
    sport && messages[`${sport}.stats.flagged.events.tooltip`]
      ? `${sport}.stats.flagged.events.tooltip`
      : "default.stats.flagged.events.tooltip";

  return [
    {
      key: FixturePlayByPlayEventColumns.EventId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.eventId" }),
      dataIndex: "eventId",
      copyable: true,
    },
    {
      key: FixturePlayByPlayEventColumns.Class,
      title: formatMessage({ id: "fixturePBPEvent.table.column.class" }),
      dataIndex: "class",
      width: 10,
    },
    {
      key: FixturePlayByPlayEventColumns.ClientId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.clientId" }),
      dataIndex: "clientId",
      width: 215,
      copyable: true,
    },
    {
      key: FixturePlayByPlayEventColumns.Clock,
      title: formatMessage({ id: "fixturePBPEvent.table.column.clock" }),
      dataIndex: "clock",
      width: 25,
    },
    {
      key: FixturePlayByPlayEventColumns.Entity,
      title: formatMessage({ id: "fixturePBPEvent.table.column.entity" }),
      dataIndex: "entityId",
      hidden: competitorType !== CompetitorTypes.Entity,
      width: 150,
      render: (_, row) => <div>{row.entity?.nameFullLocal}</div>,
    },
    {
      key: FixturePlayByPlayEventColumns.EntityId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.entityId" }),
      hidden: competitorType !== CompetitorTypes.Entity,
      dataIndex: "entityId",
      copyable: true,
      width: 300,
    },
    {
      key: FixturePlayByPlayEventColumns.EventTime,
      title: formatMessage({ id: "fixturePBPEvent.table.column.eventTime" }),
      dataIndex: "eventTime",
      width: 155,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeSecondsFormat} />,
    },
    {
      key: FixturePlayByPlayEventColumns.EventType,
      title: formatMessage({ id: "fixturePBPEvent.table.column.eventType" }),
      dataIndex: "eventType",
      width: 100,
    },
    {
      key: FixturePlayByPlayEventColumns.Fixture,
      title: formatMessage({ id: "fixturePBPEvent.table.column.fixture" }),
      dataIndex: "fixture",
      width: 300,
      render: (_, row) => <div>{row.fixture?.nameLocal}</div>,
    },
    {
      key: FixturePlayByPlayEventColumns.FixtureStartTime,
      title: formatMessage({ id: "fixturePBPEvent.table.column.fixtureStartTime" }),
      dataIndex: "fixture",
      width: 155,
      render: (_, row) => <DataTableDateColumn value={row?.fixture?.startTimeLocal} showTime format={dateTimeSecondsFormat} />,
    },
    {
      key: FixturePlayByPlayEventColumns.FixtureId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.fixtureId" }),
      dataIndex: "fixtureId",
      copyable: true,
      width: 300,
    },
    {
      key: FixturePlayByPlayEventColumns.FixtureStatus,
      title: formatMessage({ id: "fixturePBPEvent.table.column.fixtureStatus" }),
      dataIndex: "fixture",
      width: 120,
      render: (_, row) => <div>{row.fixture?.status ? formatMessage({ id: `fixtureStatus.${row.fixture?.status}` }) : ""}</div>,
    },
    {
      key: FixturePlayByPlayEventColumns.Flagged,
      title: formatMessage({ id: "fixturePBPEvent.table.column.flagged" }),
      dataIndex: "flagged",
      width: 25,
    },
    {
      key: FixturePlayByPlayEventColumns.Highlight,
      title: formatMessage({ id: "fixturePBPEvent.table.column.highlight" }),
      dataIndex: "highlight",
      width: 25,
    },
    {
      key: FixturePlayByPlayEventColumns.OfficialId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.officialId" }),
      dataIndex: "officialId",
      copyable: true,
      width: 300,
    },
    {
      key: FixturePlayByPlayEventColumns.Person,
      title: formatMessage({ id: "fixturePBPEvent.table.column.person" }),
      dataIndex: "person",
      width: 200,
      render: (_, row) => <div>{row.person?.nameFullLocal}</div>,
    },
    {
      key: FixturePlayByPlayEventColumns.PersonId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.personId" }),
      dataIndex: "personId",
      copyable: true,
      width: 300,
    },
    {
      key: FixturePlayByPlayEventColumns.PeriodId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.period" }),
      dataIndex: "periodId",
      width: 150,
      render: (value: string) => <div>{periods[value] ? formatMessage({ id: periods[value].intlKey }) : value}</div>,
    },
    {
      key: FixturePlayByPlayEventColumns.PlayId,
      title: formatMessage({ id: "fixturePBPEvent.table.column.playId" }),
      dataIndex: "playId",
      copyable: true,
      width: 300,
    },
    {
      key: FixturePlayByPlayEventColumns.Section,
      title: formatMessage({ id: "fixturePBPEvent.table.column.section" }),
      dataIndex: "section",
      width: 50,
    },
    {
      key: FixturePlayByPlayEventColumns.Received,
      title: formatMessage({ id: "fixturePBPEvent.table.column.received" }),
      dataIndex: "received",
      width: 155,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeSecondsFormat} />,
    },
    {
      key: FixturePlayByPlayEventColumns.Sequence,
      title: formatMessage({ id: "fixturePBPEvent.table.column.sequence" }),
      dataIndex: "sequence",
      width: 50,
    },
    {
      key: FixturePlayByPlayEventColumns.ShotClock,
      title: formatMessage({ id: "fixturePBPEvent.table.column.shotClock" }),
      dataIndex: "shotClock",
      width: 100,
    },
    {
      key: FixturePlayByPlayEventColumns.Status,
      title: formatMessage({ id: "fixturePBPEvent.table.column.status" }),
      dataIndex: "status",
      width: 100,
    },
    {
      key: FixturePlayByPlayEventColumns.SubType,
      title: formatMessage({ id: "fixturePBPEvent.table.column.subType" }),
      dataIndex: "subType",
      width: 100,
    },

    {
      key: FixturePlayByPlayEventColumns.Success,
      title: formatMessage({ id: "fixturePBPEvent.table.column.success" }),
      dataIndex: "success",
      width: 50,
    },
    {
      key: FixturePlayByPlayEventColumns.Date,
      title: formatMessage({ id: "fixturePBPEvent.table.column.date" }),
      dataIndex: "timestamp",
      width: 155,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeSecondsFormat} />,
    },
    {
      key: FixturePlayByPlayEventColumns.Topic,
      title: formatMessage({ id: "fixturePBPEvent.table.column.topic" }),
      dataIndex: "topic",
      width: 500,
    },
    {
      key: FixturePlayByPlayEventColumns.Zone,
      title: formatMessage({ id: "fixturePBPEvent.table.column.zone" }),
      dataIndex: "zone",
      width: 50,
    },
    {
      persistent: true,
      disableRowNavigation: true,
      render: (_, row) => (
        <Link to={`${SYS_APP_BASEURL}/game/${row.fixture.fixtureId}`} target={"_blank"}>
          <Tooltip title={formatMessage({ id: tooltipMessageId })}>
            <StyledButton type={"text"}>{formatMessage({ id: "stats" })}</StyledButton>
          </Tooltip>
        </Link>
      ),
    },
  ];
};
